import { Text } from "@ui-kit/Text/Text";
import { useAppDispatch, useAppSelector } from "@store/store";
import {
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
  FunctionComponent,
} from "react";
import { Button } from "@ui-kit/Button/Button";
import { selectPlan } from "@store/plan/planReducer";
import { getPlan, useCreatePlanMutation } from "@store/services/plan";
import {
  selectOverlayScreen,
  setOverlayScreenAction,
} from "@store/app/appReducer";
import { RadioButtonGroup } from "@ui-kit/RadioButtonGroup/RadioButtonGroup";
import { InlineDatePicker } from "@ui-kit/DatePicker/InlineDatePicker";
import { InputNumber } from "@ui-kit/InputNumber/InputNumber";

import "./PlanDatePicker.css";
import {
  OverlayFooterProps,
  OverlayRef,
} from "@modules/overlayRouter/OverlayProps";
import { useTranslation } from "react-i18next";
import { logError, logInfo } from "@lib/logger";
import dayjs from "dayjs";
import { useAddRemoveToPlan } from "@modules/components/buttons/AddRemoveButton/useAddRemoveToPlan";
type PlanDatePickerViews = "select" | "days" | "dates";

const DEFAULT_DAY_DURATION = 3;

// TODO: the widget version is different and this isn't exactly 1:1, need to update.
const headerTitles = new Map<PlanDatePickerViews, string>([
  ["select", "Add Plan Dates"],
  ["days", "Add Days"],
  ["dates", "Select Dates"],
]);

const PlanDatePicker = forwardRef<OverlayRef>((props, ref) => {
  const dispatch = useAppDispatch();
  const plan = useAppSelector(selectPlan);
  const numberOfDays = plan?.dayDuration;
  const [dayDuration, setDayDuration] = useState(numberOfDays);
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: plan?.startsOn ? dayjs(plan.startsOn).toDate() : null,
    endDate: plan?.endsOn ? dayjs(plan.endsOn).toDate() : null,
  });
  const overlayScreen = useAppSelector(selectOverlayScreen);
  const [selectedToggleIndex, setSelectedToggleIndex] = useState<0 | 1>(
    (plan?.dayDuration ?? 0) > 0 && !plan?.startsOn ? 1 : 0,
  );
  const [createPlan] = useCreatePlanMutation();
  const [onAddOrRemovePlanItem] = useAddRemoveToPlan(
    overlayScreen?.params?.entityId as number,
    overlayScreen?.params?.entityType as
      | "Place"
      | "Tour"
      | "Event"
      | "Goal"
      | "Challenge"
      | "SelfGuided",
  );

  console.log(overlayScreen?.params?.entityId);

  const handleSave = useCallback(async () => {
    let planId = plan?.id;
    if (!planId) {
      let startsOn: string | null = null;
      let endsOn: string | null = null;
      let duration: number | null = null;

      if (
        selectedToggleIndex === 0 &&
        dateRange.startDate &&
        dateRange.endDate
      ) {
        startsOn = dayjs(dateRange.startDate).format("YYYY-MM-DD");
        endsOn = dayjs(dateRange.endDate).format("YYYY-MM-DD");
        duration =
          dayjs(dateRange.endDate).diff(dayjs(dateRange.startDate), "day") + 1;
      } else if (selectedToggleIndex === 1 && dayDuration) {
        duration = dayDuration ?? DEFAULT_DAY_DURATION;
      }

      const newPlan = await createPlan({
        startsOn: startsOn,
        endsOn: endsOn,
        dayDuration: duration,
      });
      if (newPlan.error) {
        logInfo("Failed to create plan:", newPlan.error);
        return;
      }
      // Must get plan so invalidating rtk query tags re-fetches plan.
      dispatch(getPlan.initiate(newPlan.data?.id ?? -1));
      planId = newPlan.data?.id;
    }

    try {
      if (overlayScreen?.params?.entityId) {
        dispatch(
          setOverlayScreenAction({
            name: "PlanAddToDatePicker",
            params: {
              entityId: overlayScreen?.params?.entityId,
              entityType: overlayScreen?.params?.entityType,
            },
          }),
        );
      } else {
        dispatch(setOverlayScreenAction());
      }
    } catch (error) {
      logError("Failed to update plan:", error);
    }
  }, [
    plan?.id,
    createPlan,
    selectedToggleIndex,
    overlayScreen?.params?.entityId,
    overlayScreen?.params?.entityType,
    dateRange.startDate,
    dateRange.endDate,
    dayDuration,
    dispatch,
  ]);

  const handleCancel = useCallback(() => {
    console.log("handleCancel 1111");
    onAddOrRemovePlanItem(null);
    dispatch(setOverlayScreenAction());
  }, [dispatch, onAddOrRemovePlanItem]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      cancel: handleCancel,
    }),
    [handleSave, handleCancel],
  );

  const handleDateChange = useCallback((dates: [Date | null, Date | null]) => {
    setDateRange({
      startDate: dates[0] ?? null,
      endDate: dates[1] ?? null,
    });
  }, []);

  const handleNumberOfDaysChange = useCallback((value: number) => {
    setDayDuration(value);
  }, []);

  return (
    <div className="plan-date-picker-component">
      <Text variant="fontBodyMedium">
        {t("screens.plan.planDatePicker.description")}
      </Text>
      <div className="content">
        <RadioButtonGroup
          options={[{ label: "Dates" }, { label: "Days" }]}
          selectedIndex={selectedToggleIndex}
          onToggle={setSelectedToggleIndex}
        />
        {selectedToggleIndex === 0 && (
          <InlineDatePicker
            onChange={handleDateChange}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        )}
        {selectedToggleIndex === 1 && (
          <div className="plan-date-picker-number-of-days-container">
            <Text variant="fontBodyLarge">
              {t("screens.plan.planDatePicker.numberOfDays")}
            </Text>
            <InputNumber
              defaultValue={plan?.dayDuration ?? DEFAULT_DAY_DURATION}
              onChange={handleNumberOfDaysChange}
            />
          </div>
        )}
      </div>
    </div>
  );
});

PlanDatePicker.displayName = "PlanDatePicker";

const PlanDatePickerHeader = () => {
  const overlayScreen = useAppSelector(selectOverlayScreen);
  const view = (overlayScreen?.params?.view ?? "select") as PlanDatePickerViews;

  return (
    <div className="plan-date-picker-header-container">
      <div className="plan-date-picker-header-container">
        <Text variant="fontHeadingMedium" className="plan-date-picker-title">
          {headerTitles.get(view)}
        </Text>
      </div>
    </div>
  );
};

const PlanDatePickerFooter: FunctionComponent<OverlayFooterProps> = ({
  overlayRef,
}) => {
  const handleCancel = useCallback(async () => {
    if (overlayRef.current?.cancel) {
      await overlayRef.current.cancel();
    }
  }, [overlayRef]);
  const { t } = useTranslation();

  const handleSave = useCallback(async () => {
    if (overlayRef.current?.save) {
      await overlayRef.current.save();
    }
  }, [overlayRef]);

  return (
    <>
      <Button type="text" onPress={handleCancel}>
        {t("common.skip")}
      </Button>
      <Button type="primary" onPress={handleSave}>
        {t("common.save")}
      </Button>
    </>
  );
};

export { PlanDatePicker, PlanDatePickerHeader, PlanDatePickerFooter };
